// Adobe Swatch Exchange (.ase): the swatch file Illustrator, Photoshop and
// InDesign load from their Swatches panels. Big-endian throughout: a header
// ("ASEF", version 1.0, block count), then blocks: group start (0xC001), a
// color per swatch (0x0001) and group end (0xC002). Names are UTF-16BE with a
// trailing null; colors are RGB as three 32-bit floats from 0 to 1.
import { toRgb } from '../content/color.ts'

export type SwatchGroup = { name: string; colors: Array<{ name: string; hex: string }> }

class Writer {
  private bytes: number[] = []
  u16(value: number) {
    this.bytes.push((value >> 8) & 0xff, value & 0xff)
  }
  u32(value: number) {
    this.bytes.push((value >>> 24) & 0xff, (value >>> 16) & 0xff, (value >>> 8) & 0xff, value & 0xff)
  }
  f32(value: number) {
    const view = new DataView(new ArrayBuffer(4))
    view.setFloat32(0, value)
    this.bytes.push(...new Uint8Array(view.buffer))
  }
  ascii(text: string) {
    for (const char of text) this.bytes.push(char.charCodeAt(0))
  }
  name(text: string) {
    this.u16(text.length + 1)
    for (const char of text) this.u16(char.charCodeAt(0))
    this.u16(0)
  }
  push(other: Writer) {
    this.bytes.push(...other.bytes)
  }
  get length() {
    return this.bytes.length
  }
  done() {
    return new Uint8Array(this.bytes)
  }
}

/** A block: its type, the length of what follows, then the body. */
function block(out: Writer, type: number, body: Writer) {
  out.u16(type)
  out.u32(body.length)
  out.push(body)
}

export function ase(groups: SwatchGroup[]): Uint8Array<ArrayBuffer> {
  const out = new Writer()
  out.ascii('ASEF')
  out.u16(1)
  out.u16(0)
  out.u32(groups.reduce((count, group) => count + group.colors.length + 2, 0))
  for (const group of groups) {
    const start = new Writer()
    start.name(group.name)
    block(out, 0xc001, start)
    for (const color of group.colors) {
      const body = new Writer()
      body.name(color.name)
      body.ascii('RGB ')
      for (const channel of toRgb(color.hex)) body.f32(channel / 255)
      body.u16(2) // a normal (process) color, not global or spot
      block(out, 0x0001, body)
    }
    block(out, 0xc002, new Writer())
  }
  return out.done() as Uint8Array<ArrayBuffer>
}
